import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, Quote, Calendar } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FILTERS = [
  { id: "all", label: "All stories" },
  { id: "Crisis", label: "Crisis" },
  { id: "Recovery", label: "Recovery" },
  { id: "Reentry", label: "Reentry" },
  { id: "Family", label: "Family" },
  { id: "Provider", label: "Provider" },
];

export default function Stories() {
  const [stories, setStories] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = filter === "all" ? "" : `?category=${filter}`;
    axios
      .get(`${API}/stories${params}`)
      .then((r) => setStories(r.data.stories || []))
      .catch(() => setStories([]))
      .finally(() => setLoading(false));
  }, [filter]);

  return (
    <div data-testid="stories-page" className="bg-white">
      {/* HERO */}
      <section className="py-20 lg:py-28 bg-gradient-hero" data-testid="stories-hero">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <p className="eyebrow mb-5">Community voices</p>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl text-navy leading-[1.05]">
            Real <span className="hl-teal">stories.</span>{" "}<br />Real <span className="hl-teal">people.</span>
          </h1>
          <p className="mt-7 text-lg sm:text-xl text-muted-ink leading-relaxed max-w-2xl mx-auto">
            The hardest moments. The smallest mercies. The unexpected ways people found their way through.
            These stories are shared with permission, in the storytellers' own words.
          </p>
        </div>
      </section>

      {/* FILTERS */}
      <section className="py-8 border-y border-slate-200 bg-cream/40" data-testid="stories-filters">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex flex-wrap gap-2 justify-center">
            {FILTERS.map((f) => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-bold transition ${
                  filter === f.id
                    ? "bg-navy text-white"
                    : "bg-white border border-slate-200 text-navy hover:border-teal"
                }`}
                data-testid={`filter-${f.id}`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* GRID */}
      <section className="py-16 lg:py-24" data-testid="stories-list">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {loading ? (
            <p className="text-center text-muted-ink" data-testid="stories-loading">Loading stories...</p>
          ) : stories.length === 0 ? (
            <p className="text-center text-muted-ink" data-testid="stories-empty">No stories in this category yet.</p>
          ) : (
            <div className="grid md:grid-cols-2 gap-5 lg:gap-6">
              {stories.map((s) => (
                <Link
                  key={s.id}
                  to={`/stories/${s.id}`}
                  className="gh-card !p-8 group flex flex-col"
                  data-testid={`story-card-${s.id}`}
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className="eyebrow">{s.category}</span>
                    <span className="text-xs text-muted-ink flex items-center gap-1.5">
                      <Calendar className="w-3.5 h-3.5" /> {s.date}
                    </span>
                  </div>
                  <Quote className="w-7 h-7 text-teal mb-4" fill="currentColor" />
                  <h3 className="font-display text-2xl text-navy leading-tight mb-4 group-hover:text-teal transition-colors">
                    "{s.title}"
                  </h3>
                  <p className="text-muted-ink leading-relaxed flex-1 mb-6">{s.summary}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-slate-200">
                    <span className="text-sm text-navy font-semibold">— {s.by}</span>
                    <span className="text-teal font-bold inline-flex items-center gap-1 text-sm group-hover:gap-2 transition-all">
                      Read <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SHARE CTA */}
      <section className="py-20 bg-navy text-white relative overflow-hidden" data-testid="stories-share-cta">
        <div className="absolute top-0 right-0 w-[400px] h-[400px] bg-teal/15 rounded-full blur-3xl" aria-hidden="true" />
        <div className="relative max-w-3xl mx-auto px-6 lg:px-10 text-center">
          <Quote className="w-10 h-10 text-teal mx-auto mb-6" fill="currentColor" />
          <h2 className="font-display text-4xl sm:text-5xl text-white mb-5 leading-[1.1]">
            Your story could <span className="hl-teal">save someone tonight.</span>
          </h2>
          <p className="text-white/80 mb-9 max-w-xl mx-auto">
            Sharing what you've lived through — even anonymously — can be the moment another person realizes
            they're not alone. We'll handle it with care.
          </p>
          <Link to="/contact" className="btn-primary" data-testid="stories-share-button">
            Share your story <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
